/**
 * useCSVImport.ts
 * 
 * Hook personalizado para importar contactos desde archivos CSV.
 * Maneja parsing, validación, detección de duplicados y preview.
 * 
 * @version 1.0.0
 * @date 2024-11-06
 */

import { useState, useCallback } from 'react';
import { ContactDB } from '../data/mockContactsDatabase';

// ==================== 📋 INTERFACES ====================

export interface CSVRow {
  [key: string]: string;
}

export interface ParsedContact {
  name: string;
  phone: string;
  email?: string;
  company?: string;
  tags?: string[];
  groups?: string[];
  isValid: boolean;
  errors: string[];
  isDuplicate: boolean;
  duplicateOf?: string;
}

export interface ImportResult {
  total: number;
  success: number;
  skipped: number;
  errors: number;
  contacts: ParsedContact[];
}

export interface ColumnMapping {
  name?: string;
  phone?: string;
  email?: string;
  company?: string;
  tags?: string;
  groups?: string;
}

export interface UseCSVImportResult {
  // Estados
  isProcessing: boolean;
  progress: number;
  currentStep: 'idle' | 'parsing' | 'validating' | 'importing' | 'complete';
  
  // Datos
  rawData: CSVRow[];
  parsedContacts: ParsedContact[];
  columnMapping: ColumnMapping;
  detectedColumns: string[];
  importResult: ImportResult | null;
  
  // Acciones
  parseCSV: (file: File) => Promise<void>;
  setColumnMapping: (mapping: ColumnMapping) => void;
  validateContacts: (existingContacts: ContactDB[]) => void;
  importContacts: (onImport: (contact: Omit<ContactDB, 'id' | 'created_at' | 'updated_at'>) => Promise<ContactDB | void>) => Promise<ImportResult>;
  reset: () => void;
}

// ==================== 🔧 HELPERS ====================

/**
 * Parse CSV string a array de objetos
 */
const parseCSVString = (csvString: string): CSVRow[] => {
  const lines = csvString.split('\n').filter(line => line.trim());
  if (lines.length === 0) return [];

  // Primera línea son los headers
  const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
  
  // Parsear cada línea
  const rows: CSVRow[] = [];
  for (let i = 1; i < lines.length; i++) {
    const values = lines[i].split(',').map(v => v.trim().replace(/"/g, ''));
    const row: CSVRow = {};
    
    headers.forEach((header, index) => {
      row[header] = values[index] || '';
    });
    
    rows.push(row);
  }
  
  return rows;
};

/**
 * Detectar columnas automáticamente
 */
const detectColumns = (headers: string[]): ColumnMapping => {
  const mapping: ColumnMapping = {};
  
  headers.forEach(header => {
    const lower = header.toLowerCase();
    
    // Detectar nombre
    if (lower.includes('nombre') || lower.includes('name')) {
      mapping.name = header;
    }
    // Detectar teléfono
    else if (lower.includes('telefono') || lower.includes('phone') || lower.includes('celular') || lower.includes('movil')) {
      mapping.phone = header;
    }
    // Detectar email
    else if (lower.includes('email') || lower.includes('correo') || lower.includes('mail')) {
      mapping.email = header;
    }
    // Detectar empresa
    else if (lower.includes('empresa') || lower.includes('company') || lower.includes('organizacion')) {
      mapping.company = header;
    }
    // Detectar tags
    else if (lower.includes('tag') || lower.includes('etiqueta')) {
      mapping.tags = header;
    }
    // Detectar grupos
    else if (lower.includes('grupo') || lower.includes('group')) {
      mapping.groups = header;
    }
  });
  
  return mapping;
};

/**
 * Validar teléfono
 */
const validatePhone = (phone: string): boolean => {
  if (!phone) return false;
  
  // Limpiar teléfono
  const cleaned = phone.replace(/\s+/g, '').replace(/[^\d+]/g, '');
  
  // Debe tener al menos 8 dígitos
  if (cleaned.length < 8) return false;
  
  // Si tiene código de país, debe empezar con +
  if (cleaned.length > 10 && !cleaned.startsWith('+')) return false;
  
  return true;
};

/**
 * Validar email
 */
const validateEmail = (email: string): boolean => {
  if (!email) return true; // Email es opcional
  
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Normalizar teléfono
 */
const normalizePhone = (phone: string): string => {
  let cleaned = phone.replace(/\s+/g, '').replace(/[^\d+]/g, '');
  
  // Si no tiene código de país y es chileno (8-9 dígitos), agregar +56
  if (!cleaned.startsWith('+') && cleaned.length >= 8 && cleaned.length <= 9) {
    cleaned = '+56' + cleaned;
  }
  
  return cleaned;
};

// ==================== 🎣 HOOK ====================

export const useCSVImport = (): UseCSVImportResult => {
  // Estados
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState<'idle' | 'parsing' | 'validating' | 'importing' | 'complete'>('idle');
  
  const [rawData, setRawData] = useState<CSVRow[]>([]);
  const [parsedContacts, setParsedContacts] = useState<ParsedContact[]>([]);
  const [columnMapping, setColumnMapping] = useState<ColumnMapping>({});
  const [detectedColumns, setDetectedColumns] = useState<string[]>([]);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);

  // ==================== 📥 PARSE CSV ====================

  const parseCSV = useCallback(async (file: File): Promise<void> => {
    setIsProcessing(true);
    setCurrentStep('parsing');
    setProgress(0);

    try {
      // Leer archivo
      const text = await file.text();
      
      // Parsear CSV
      const rows = parseCSVString(text);
      
      if (rows.length === 0) {
        throw new Error('El archivo CSV está vacío');
      }
      
      setRawData(rows);
      
      // Detectar columnas
      const headers = Object.keys(rows[0]);
      setDetectedColumns(headers);
      
      const detectedMapping = detectColumns(headers);
      setColumnMapping(detectedMapping);
      
      setProgress(100);
      setCurrentStep('idle');
      
    } catch (error: any) {
      console.error('Error parsing CSV:', error);
      setCurrentStep('idle');
      throw error;
    } finally {
      setIsProcessing(false);
    }
  }, []);

  // ==================== ✅ VALIDATE CONTACTS ====================

  const validateContacts = useCallback((existingContacts: ContactDB[]) => {
    setIsProcessing(true);
    setCurrentStep('validating');
    setProgress(0);

    const validated: ParsedContact[] = [];
    const total = rawData.length;

    rawData.forEach((row, index) => {
      const errors: string[] = [];
      
      // Obtener valores según mapping
      const name = columnMapping.name ? row[columnMapping.name]?.trim() : '';
      const phone = columnMapping.phone ? row[columnMapping.phone]?.trim() : '';
      const email = columnMapping.email ? row[columnMapping.email]?.trim() : '';
      const company = columnMapping.company ? row[columnMapping.company]?.trim() : '';
      const tagsStr = columnMapping.tags ? row[columnMapping.tags]?.trim() : '';
      const groupsStr = columnMapping.groups ? row[columnMapping.groups]?.trim() : '';
      
      // Validar nombre
      if (!name) {
        errors.push('Nombre requerido');
      }
      
      // Validar teléfono
      if (!phone) {
        errors.push('Teléfono requerido');
      } else if (!validatePhone(phone)) {
        errors.push('Formato de teléfono inválido');
      }
      
      // Validar email
      if (email && !validateEmail(email)) {
        errors.push('Formato de email inválido');
      }
      
      // Normalizar teléfono
      const normalizedPhone = phone ? normalizePhone(phone) : '';
      
      // Detectar duplicados
      const duplicate = existingContacts.find(c => 
        c.phone.replace(/\s+/g, '') === normalizedPhone.replace(/\s+/g, '')
      );
      
      const isDuplicate = !!duplicate;
      
      // Parsear tags y grupos
      const tags = tagsStr ? tagsStr.split(/[,;|]/).map(t => t.trim()).filter(Boolean) : [];
      const groups = groupsStr ? groupsStr.split(/[,;|]/).map(g => g.trim()).filter(Boolean) : [];
      
      validated.push({
        name,
        phone: normalizedPhone,
        email: email || undefined,
        company: company || undefined,
        tags: tags.length > 0 ? tags : undefined,
        groups: groups.length > 0 ? groups : undefined,
        isValid: errors.length === 0 && !isDuplicate,
        errors,
        isDuplicate,
        duplicateOf: duplicate?.id
      });
      
      // Actualizar progreso
      setProgress(Math.round(((index + 1) / total) * 100));
    });

    setParsedContacts(validated);
    setCurrentStep('idle');
    setIsProcessing(false);
  }, [rawData, columnMapping]);

  // ==================== 📤 IMPORT CONTACTS ====================

  const importContacts = useCallback(async (
    onImport: (contact: Omit<ContactDB, 'id' | 'created_at' | 'updated_at'>) => Promise<ContactDB | void>
  ): Promise<ImportResult> => {
    setIsProcessing(true);
    setCurrentStep('importing');
    setProgress(0);

    const result: ImportResult = {
      total: parsedContacts.length,
      success: 0,
      skipped: 0,
      errors: 0,
      contacts: []
    };

    const validContacts = parsedContacts.filter(c => c.isValid);
    const total = validContacts.length;

    for (let i = 0; i < validContacts.length; i++) {
      const contact = validContacts[i];
      
      try {
        await onImport({
          name: contact.name,
          phone: contact.phone,
          email: contact.email,
          company: contact.company,
          tags: contact.tags || [],
          groups: contact.groups || [],
          status: 'active',
          source: 'import',
          total_messages_sent: 0,
          total_messages_received: 0
        });
        
        result.success++;
      } catch (error) {
        console.error('Error importing contact:', error);
        result.errors++;
      }
      
      // Actualizar progreso
      setProgress(Math.round(((i + 1) / total) * 100));
    }

    // Contar skipped (duplicados + inválidos)
    result.skipped = parsedContacts.length - validContacts.length;

    setImportResult(result);
    setCurrentStep('complete');
    setIsProcessing(false);

    return result;
  }, [parsedContacts]);

  // ==================== 🔄 RESET ====================

  const reset = useCallback(() => {
    setIsProcessing(false);
    setProgress(0);
    setCurrentStep('idle');
    setRawData([]);
    setParsedContacts([]);
    setColumnMapping({});
    setDetectedColumns([]);
    setImportResult(null);
  }, []);

  // ==================== 📤 RETURN ====================

  return {
    isProcessing,
    progress,
    currentStep,
    rawData,
    parsedContacts,
    columnMapping,
    detectedColumns,
    importResult,
    parseCSV,
    setColumnMapping,
    validateContacts,
    importContacts,
    reset
  };
};

export default useCSVImport;